import { Component, NgZone, OnDestroy } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { Input, HostBinding, Output, EventEmitter } from '@angular/core';
import { ElementRef, ViewChild } from '@angular/core';

import { viewService } from '@unpispas/upp-base';

/**
 * A reusable scrollable component for Angular applications.
 * 
 * This component provides a customizable scroll behavior for its content, with the ability
 * to configure scroll directions (`x`, `y`, both, or none). CSS classes are applied dynamically
 * based on the input properties and the state of the `viewService`.
 * 
 * ### Example Usage:
 * ```html
 * <upp-scrollable scrollbar="x">
 *     <div>Scrollable content here</div>
 * </upp-scrollable>
 * ```
 * 
 * ### Features:
 * - Dynamically controls scroll visibility (horizontal, vertical, or both).
 * - Integrates with `viewService` for viewport-aware adjustments.
 * - Provides scrollbar customization via CSS classes.
 * - Optional cap: set **`--upp-scrollable-max-height`** on the host (from a parent stylesheet)
 *   so the panel scrolls inside when content exceeds that height (e.g. `upp-select` list).
 */
@Component({
    selector: 'upp-scrollable',
    templateUrl: './upp-scrollable.html',
    styleUrls: ['./upp-scrollable.scss'],
})
export class UppScrollableComponent implements OnDestroy {
    private static readonly DRAG_ACTIVATION_PX = 4;
    /**
     * Defines the scrollable direction for the component.
     * 
     * @default 'none'
     * 
     * - `'none'`: Both scrollbar tracks are visually hidden; content can still scroll when the layout constrains the area (e.g. `upp-form`).
     * - `'x'`: Horizontal scrollbar is enabled; vertical scrolling is disabled. The host uses **content height**
 *   (`height: auto`) so it does not expand inside column flex layouts (unlike `'y'`, which keeps `height: 100%`
 *   for fill-area lists). Override `--upp-scrollable-bottom-gap` on the host if you need extra end padding.
 *   Override `--upp-scrollable-top-gap` on the host if you need extra start padding (default `0px`).
     * - `'y'`: Vertical scrollbar is enabled; horizontal scrolling is disabled.
     * - `'both'`: Both horizontal and vertical scrollbars are enabled.
     */    
    @Input() scrollbar: 'none' | 'x' | 'y' | 'both' = 'none';
    /**
     * Layout of the projected content slot.
     *
     * - `'auto'` (default): the slot is a normal block; children stack and the area scrolls
     *   when it overflows (current behavior).
     * - `'fill'`: the slot becomes a vertical flex column that stretches to the full scroll
     *   height, so a projected child can grow to fill the available space (e.g. `upp-form`
     *   in `layout='fill'`). Consumers no longer need to penetrate `.upp-scrollable-content`
     *   with `::ng-deep`.
     */
    @Input() content: 'auto' | 'fill' = 'auto';
    @Output() OnScroll = new EventEmitter<Event>();
    
    @ViewChild('contentRef') private _contentRef: ElementRef<HTMLDivElement> | null = null;

    private _dragging = false;
    private _dragStartX = 0;
    private _dragStartY = 0;
    private _dragStartScrollLeft = 0;
    private _dragStartScrollTop = 0;
    private _dragPointerId: number | null = null;
    private _dragActivated = false;
    private _dragListenerTarget: HTMLDivElement | null = null;
    private _onPointerMove: ((event: PointerEvent) => void) | null = null;
    private _onPointerUp: ((event: PointerEvent) => void) | null = null;
    private _onPointerCancel: ((event: PointerEvent) => void) | null = null;

    /**
     * Dynamically generates CSS classes based on the `scrollbar` input value.
     * 
     * - Adds `--no-scrollbar-x` when horizontal scrolling is disabled.
     * - Adds `--no-scrollbar-y` when vertical scrolling is disabled.
     * - Both classes are added when `scrollbar` is set to `'none'`.
     * 
     * @returns A space-separated string of CSS class names.
     */    
    @HostBinding('class') get panelClasses(): string {
        const tokens: string[] = [];
        switch (this.scrollbar) {
            case 'none':
                tokens.push('--no-scrollbar-x', '--no-scrollbar-y');
                break;
            case 'x':
                tokens.push('--no-scrollbar-y', '--scroll-x-only');
                break;
            case 'y':
                tokens.push('--no-scrollbar-x');
                break;
            case 'both':
                break;
        }
        if (this.content === 'fill') {
            tokens.push('--fill-content');
        }
        return tokens.join(' ').trim();
    }

    /**
     * Constructor for the UppScrollableComponent.
     * 
     * @param view A service providing view-related state, such as whether the current view
     *             is on a mobile device or has a visible scrollbar.
     */    
    constructor(
        public view: viewService,
        private zone: NgZone,
        private change: ChangeDetectorRef,
    ){
        // nothing to do
    }

    ngOnDestroy(): void {
        this._DetachDragListeners();
    }

    get IsMouseDragging(): boolean {
        return this._dragging;
    }

    get IsMouseDraggable(): boolean {
        return this.scrollbar === 'x' || this.scrollbar === 'y' || this.scrollbar === 'both';
    }

    OnContentScroll(event: Event): void {
        this.OnScroll.emit(event);
    }

    OnPointerDown(event: PointerEvent): void {
        if (!this.IsMouseDraggable || event.pointerType !== 'mouse' || event.button !== 0){
            return;
        }

        const content = this._contentRef?.nativeElement;
        if (!content){
            return;
        }

        this._dragging = true;
        this._dragPointerId = event.pointerId;
        this._dragStartX = event.clientX;
        this._dragStartY = event.clientY;
        this._dragStartScrollLeft = content.scrollLeft;
        this._dragStartScrollTop = content.scrollTop;
        this._dragActivated = false;
        this._AttachDragListeners(content);
    }

    OnPointerMove(event: PointerEvent): void {
        if (!this._dragging || this._dragPointerId !== event.pointerId){
            return;
        }

        const content = this._contentRef?.nativeElement;
        if (!content){
            return;
        }

        const deltaX = event.clientX - this._dragStartX;
        const deltaY = event.clientY - this._dragStartY;
        const canScrollX = content.scrollWidth > content.clientWidth;
        const canScrollY = content.scrollHeight > content.clientHeight;
        const deltaXAbs = Math.abs(deltaX);
        const deltaYAbs = Math.abs(deltaY);
        const activationDistance = this._DragActivationDistance(deltaXAbs, deltaYAbs);

        if (!this._dragActivated) {
            if (activationDistance < UppScrollableComponent.DRAG_ACTIVATION_PX) {
                return;
            }

            this._dragActivated = true;
            if (typeof content.setPointerCapture === 'function'){
                content.setPointerCapture(event.pointerId);
            }
        }

        if ((this.scrollbar === 'x' || this.scrollbar === 'both') && canScrollX){
            content.scrollLeft = this._dragStartScrollLeft - deltaX;
        }
        if ((this.scrollbar === 'y' || this.scrollbar === 'both') && canScrollY){
            content.scrollTop = this._dragStartScrollTop - deltaY;
        }
        event.preventDefault();
    }

    OnPointerUp(event: PointerEvent): void {
        this._EndMouseDrag(event);
    }

    OnPointerCancel(event: PointerEvent): void {
        this._EndMouseDrag(event);
    }

    /**
     * Block the native HTML5 drag-and-drop on projected children (e.g. images, links) so
     * the user-driven mouse drag is dedicated to the scroll behavior.
     */
    OnDragStart(event: DragEvent): void {
        if (!this.IsMouseDraggable){
            return;
        }
        event.preventDefault();
    }

    private _EndMouseDrag(event: PointerEvent): void {
        if (!this._dragging || this._dragPointerId !== event.pointerId){
            return;
        }

        const content = this._contentRef?.nativeElement;
        if (content && this._dragActivated && typeof content.releasePointerCapture === 'function'){
            if ((typeof content.hasPointerCapture !== 'function') || content.hasPointerCapture(event.pointerId)){
                content.releasePointerCapture(event.pointerId);
            }
        }

        const _wasDragging = this._dragging;

        this._dragging = false;
        this._dragPointerId = null;
        this._dragActivated = false;
        this._DetachDragListeners();

        if (_wasDragging){
            this.change.markForCheck();
        }
    }

    private _AttachDragListeners(content: HTMLDivElement): void {
        this._DetachDragListeners();

        this._dragListenerTarget = content;
        
        const _onPointerMove = (event: PointerEvent) => {
            this.OnPointerMove(event);
        };

        const _onPointerUp = (event: PointerEvent) => {
            this.OnPointerUp(event);
        };

        const _onPointerCancel = (event: PointerEvent) => {
            this.OnPointerCancel(event);
        };

        this._onPointerMove = _onPointerMove;
        this._onPointerUp = _onPointerUp;
        this._onPointerCancel = _onPointerCancel;

        this.zone.runOutsideAngular(() => {
            content.addEventListener('pointermove', _onPointerMove);
            content.addEventListener('pointerup', _onPointerUp);
            content.addEventListener('pointercancel', _onPointerCancel);
        });
    }

    private _DetachDragListeners(): void {
        const _content = this._dragListenerTarget;
        
        const _onPointerMove = this._onPointerMove;
        const _onPointerUp = this._onPointerUp;
        const _onPointerCancel = this._onPointerCancel;
        
        if (!_content || !_onPointerMove || !_onPointerUp || !_onPointerCancel){
            return;
        }

        _content.removeEventListener('pointermove', _onPointerMove);
        _content.removeEventListener('pointerup', _onPointerUp);
        _content.removeEventListener('pointercancel', _onPointerCancel);

        this._dragListenerTarget = null;
        this._onPointerMove = null;
        this._onPointerUp = null;
        this._onPointerCancel = null;
    }

    private _DragActivationDistance(deltaXAbs: number, deltaYAbs: number): number {
        switch (this.scrollbar) {
            case 'x':
                return deltaXAbs;
            case 'y':
                return deltaYAbs;
            case 'both':
                return Math.max(deltaXAbs, deltaYAbs);
            case 'none':
                return 0;
        }
    }
}
